import React from "react";
import { useParams } from "react-router-dom";

import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import PageTitle from "../../../components/PageTitle";
import { SideNavigationLink } from "../../../components/SideNavigation";

import { useCompanySettings, useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import ErrorPage from "../../Error";
import AllCompetencies from "./SubPages/AllCompetencies";
import { CompanyCompetenciesNavigationLinks } from "./constants";

const CompanyCompetencies = (): React.ReactElement => {
	const { page = "all" } = useParams<{ page: string }>();
	const { value: ffCompetencies, query: ffQuery } = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	// One primary-role resolution covers both the page gate and the kebab actions on every
	// row (admins hold the permissions company-wide) — no per-row object RBAC on this page.
	const { data: assignmentPermissions, isLoading: loadingAssignmentPermissions } =
		useRbacPrimaryRolePermissions("competency_assignment");
	const { data: settings } = useCompanySettings();

	if (ffQuery?.isLoading || loadingAssignmentPermissions) return <ComponentLoader />;
	if (!ffCompetencies) return <ErrorPage code={403} />;

	const navLinks: SideNavigationLink[] = assignmentPermissions?.includes("competencies:viewCompanyAssignments")
		? CompanyCompetenciesNavigationLinks
		: [];

	if (!navLinks.find((link) => link.key === page)) {
		return <ErrorPage code={403} />;
	}

	const canUpdateCurrentLevel = !!assignmentPermissions?.includes("competencies:updateAssignmentLevel");
	const canExportCsv = !!assignmentPermissions?.includes("competencies:exportCompanyAssignmentsCsv");

	return (
		<>
			<PageTitle
				title={`Competencies${settings?.company?.name ? ` - ${settings.company.name}` : ""} - PerformYard`}
			/>
			<div>
				{page === "all" && (
					<AllCompetencies
						navLinks={navLinks}
						canUpdateCurrentLevel={canUpdateCurrentLevel}
						canExportCsv={canExportCsv}
					/>
				)}
			</div>
		</>
	);
};

export default CompanyCompetencies;
